import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppProvider } from '../contexts/AppContext';
import { normalizeVenueId, resolveVenueIdFromLocation } from '../utils/venueParam';
import { updateVenueManifest } from '../utils/DynamicPWA';
import { buildVenuePath } from '../utils/venueRouting';
import { getLegacyLinkTarget, hasVenueParameter, readLegacyVenue } from '../utils/legacyVenueLink';

const VenueGate = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const venueId = resolveVenueIdFromLocation({ search: location.search, hostname: window.location.hostname });
  const routeKey = location.pathname + location.search + location.hash;
  const [resolution, setResolution] = useState(null);
  const [retry, setRetry] = useState(0);
  const [venueSearch, setVenueSearch] = useState('');
  const canResolve = !venueId && !hasVenueParameter(location.search);
  const chooseVenue = id => navigate(buildVenuePath(routeKey, id), { replace: true });
  useEffect(() => {
    updateVenueManifest(venueId);
    if (venueId) {
      try { localStorage.setItem('venue_id', venueId); } catch { }
    }
  }, [venueId]);
  useEffect(() => {
    if (!canResolve) return;
    const cached = readLegacyVenue();
    const linkTarget = getLegacyLinkTarget(location.pathname);
    const controller = new AbortController();
    let active = true;
    setVenueSearch('');
    setResolution({ routeKey, loading: true });
    const resolve = async () => {
      try {
        const endpoint = linkTarget ? `/public/resolve-venue/${linkTarget.kind}/${encodeURIComponent(linkTarget.id)}` : '/public/resolve-venue';
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}${endpoint}`, { signal: controller.signal });
        if (!response.ok) throw new Error('Unable to resolve link');
        const data = await response.json();
        if (!Array.isArray(data.venues)) throw new Error('Invalid response');
        const venues = data.venues.filter(venue => venue && normalizeVenueId(venue.id));
        if (!active) return;
        const historicalVenue = venues.find(venue => venue.id === cached);
        if (!linkTarget && cached && !historicalVenue) {
          try {
            if (readLegacyVenue() === cached) localStorage.removeItem('venue_id');
          } catch { }
        }
        const selected = historicalVenue || (venues.length === 1 ? venues[0] : null);
        if (selected) navigate(buildVenuePath(routeKey, selected.id), { replace: true });
        else setResolution({ routeKey, venues });
      } catch {
        if (active) setResolution({ routeKey, error: true });
      }
    };
    resolve();
    return () => { active = false; controller.abort(); };
  }, [canResolve, routeKey, location.pathname, navigate, retry]);
  if (!venueId) {
    const result = resolution?.routeKey === routeKey ? resolution : null;
    if (canResolve && (!result || result.loading)) return <main className="p-6 text-center">Opening your venue...</main>;
    if (canResolve && result?.error) return <main className="p-6 text-center"><p>Unable to open this link. Please try again.</p><button onClick={() => setRetry(value => value + 1)}>Retry</button></main>;
    if (canResolve && result?.venues?.length > 1) {
      const matches = result.venues.filter(venue => (venue.name || venue.id).toLowerCase().includes(venueSearch.trim().toLowerCase()));
      return <main className="p-6 text-center"><h1>Choose your venue</h1><label className="block mt-4">Find your venue<input className="block mx-auto border p-2" value={venueSearch} onChange={event => setVenueSearch(event.target.value)} /></label>{matches.map(venue => <button className="block mx-auto p-3" key={venue.id} onClick={() => chooseVenue(venue.id)}>{venue.name || venue.id}</button>)}{!matches.length && <p>No venues match your search.</p>}</main>;
    }
    return (
      <main className="flex min-h-screen items-center justify-center bg-gray-100 p-6 text-center">
        <div>
          <h1 className="text-2xl font-semibold mb-4">This link is missing venue information</h1>
          <p>Please open the full venue link or scan your venue's QR code.</p>
        </div>
      </main>
    );
  }
  return <AppProvider key={venueId} venueId={venueId}>{children}</AppProvider>;
};

export default VenueGate;
